import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { AccountBalanceWallet as WalletIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import {
  StripePayout,
  formatStripePayoutAmount,
} from '../../models/Organization';
import LedgerPagination from '../common/LedgerPagination';
import {
  DEFAULT_LEDGER_ROWS_PER_PAGE,
} from '../common/LedgerRowsPerPage';

type PayoutSection = 'upcoming' | 'past';

function payoutStatusColor(
  status: string,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'info';
    case 'in_transit':
      return 'warning';
    case 'failed':
    case 'canceled':
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function PayoutRow({
  payout,
  language,
  section,
}: {
  payout: StripePayout;
  language: string;
  section: PayoutSection;
}) {
  const { t } = useTranslation();
  const statusKey = payout.status.toLowerCase();
  const arrival = formatDate(payout.arrivalAt);
  const created = formatDate(payout.createdAt);
  const failure =
    payout.failureMessage?.trim() ||
    (payout.failureCode
      ? t('stripeConnect.payouts.failureCode', 'Failure: {{code}}', {
          code: payout.failureCode,
        })
      : null);

  let dateLine: string;
  if (arrival) {
    dateLine =
      section === 'upcoming'
        ? t('stripeConnect.payouts.arrivesOn', 'Expected {{date}}', { date: arrival })
        : t('stripeConnect.payouts.arrivedOn', 'Arrived {{date}}', { date: arrival });
  } else if (created) {
    dateLine = t('stripeConnect.payouts.createdOn', 'Created {{date}}', { date: created });
  } else {
    dateLine = t('stripeConnect.payouts.noDate', 'Date unavailable');
  }

  return (
    <Stack
      direction="row"
      spacing={{ xs: 1.25, sm: 1.75 }}
      alignItems="flex-start"
      sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}
    >
      <Chip
        label={t(`stripeConnect.payouts.status.${statusKey}`, payout.status)}
        color={payoutStatusColor(payout.status)}
        sx={{
          fontWeight: 800,
          fontSize: '0.8125rem',
          height: 32,
          px: 0.5,
          flexShrink: 0,
          mt: 0.15,
          '& .MuiChip-label': { px: 1.25 },
        }}
      />

      <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body1" fontWeight={700} sx={{ minWidth: 0 }}>
          {payout.description?.trim() ||
            t('stripeConnect.payouts.untitled', 'Bank payout')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dateLine}
          {payout.automatic
            ? ` · ${t('stripeConnect.payouts.automatic', 'Automatic')}`
            : ` · ${t('stripeConnect.payouts.manual', 'Manual')}`}
        </Typography>
        {failure && (
          <Typography variant="body2" color="error.main">
            {failure}
          </Typography>
        )}
      </Stack>

      <Typography
        variant="body1"
        fontWeight={800}
        sx={{ letterSpacing: '-0.01em', flexShrink: 0, pt: 0.25 }}
      >
        {formatStripePayoutAmount(payout.amount, payout.currencyCode, language)}
      </Typography>
    </Stack>
  );
}

/**
 * Stripe Connect bank payout ledger for host Integrations → Stripe.
 * Aesthetic: calm operations ledger — upcoming vs past, status chips, no derived estimates.
 */
const StripePayoutHistory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [upcoming, setUpcoming] = useState<StripePayout[]>([]);
  const [past, setPast] = useState<StripePayout[]>([]);
  const [section, setSection] = useState<PayoutSection>('past');
  const [hasMore, setHasMore] = useState(false);
  const [nextStartingAfter, setNextStartingAfter] = useState<string | null>(null);
  /** Cursor used for each page index; index 0 is always undefined (first page). */
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LEDGER_ROWS_PER_PAGE);
  /** True only while the first page (or a full reset) is loading with no list to keep. */
  const [initialLoading, setInitialLoading] = useState(true);
  /** True while fetching another page — keep showing the current list to avoid layout jump. */
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** First effect run (and explicit retry) may replace the list; later rows-per-page resets keep it. */
  const isFirstLoadRef = React.useRef(true);

  const loadAtCursor = useCallback(
    async (
      startingAfter: string | undefined,
      options?: {
        resetSection?: boolean;
        nextPageIndex?: number;
        nextCursorStack?: (string | undefined)[];
        /** When true, replace the list with a spinner (first load / retry). */
        replaceList?: boolean;
      },
    ) => {
      const replaceList = Boolean(options?.replaceList);
      try {
        if (replaceList) {
          setInitialLoading(true);
        } else {
          setPageLoading(true);
        }
        setError(null);
        const page = await hostApiClient.getStripePayouts({
          limit: rowsPerPage,
          startingAfter,
        });
        setUpcoming(page.upcoming);
        setPast(page.past);
        setHasMore(page.hasMore);
        setNextStartingAfter(page.nextStartingAfter);
        if (options?.nextPageIndex != null) {
          setPageIndex(options.nextPageIndex);
        }
        if (options?.nextCursorStack) {
          setCursorStack(options.nextCursorStack);
        }
        if (options?.resetSection) {
          setSection(page.past.length > 0 ? 'past' : 'upcoming');
        }
      } catch (err: unknown) {
        const status =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined;
        if (status === 404) {
          setUpcoming([]);
          setPast([]);
          setHasMore(false);
          setNextStartingAfter(null);
          setPageIndex(0);
          setCursorStack([undefined]);
          setSection('past');
          setError(null);
          return;
        }
        console.error('Error loading Stripe payouts:', err);
        setError(t('stripeConnect.payouts.fetchError', 'Failed to load payouts'));
        if (replaceList) {
          setUpcoming([]);
          setPast([]);
          setHasMore(false);
          setNextStartingAfter(null);
          setPageIndex(0);
          setCursorStack([undefined]);
        }
      } finally {
        setInitialLoading(false);
        setPageLoading(false);
      }
    },
    [rowsPerPage, t],
  );

  useEffect(() => {
    const replaceList = isFirstLoadRef.current;
    isFirstLoadRef.current = false;
    void loadAtCursor(undefined, {
      resetSection: replaceList,
      nextPageIndex: 0,
      nextCursorStack: [undefined],
      replaceList,
    });
  }, [loadAtCursor]);

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
  };

  const handlePrevious = () => {
    if (pageIndex <= 0 || pageLoading || initialLoading) return;
    const prevIndex = pageIndex - 1;
    void loadAtCursor(cursorStack[prevIndex], { nextPageIndex: prevIndex });
  };

  const handleNext = () => {
    if (!hasMore || !nextStartingAfter || pageLoading || initialLoading) return;
    const nextIndex = pageIndex + 1;
    const nextStack = cursorStack.slice(0, pageIndex + 1);
    nextStack[nextIndex] = nextStartingAfter;
    void loadAtCursor(nextStartingAfter, {
      nextPageIndex: nextIndex,
      nextCursorStack: nextStack,
    });
  };

  const handleRetry = () => {
    isFirstLoadRef.current = false;
    void loadAtCursor(undefined, {
      resetSection: true,
      nextPageIndex: 0,
      nextCursorStack: [undefined],
      replaceList: true,
    });
  };

  const busy = initialLoading || pageLoading;
  const isEmpty = upcoming.length === 0 && past.length === 0;
  const visiblePayouts = section === 'upcoming' ? upcoming : past;
  const sectionEmpty = !isEmpty && visiblePayouts.length === 0;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              flexShrink: 0,
            }}
          >
            <WalletIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              component="h2"
              fontWeight={800}
              sx={{ letterSpacing: '-0.01em' }}
            >
              {t('stripeConnect.payouts.title', 'Payouts')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                'stripeConnect.payouts.subtitle',
                'Bank transfers Stripe has scheduled or completed for this Connect account.',
              )}
            </Typography>
          </Box>
        </Stack>

        {!initialLoading && !error && !isEmpty && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={section}
            onChange={(_event, value: PayoutSection | null) => {
              if (value != null) setSection(value);
            }}
            aria-label={t(
              'stripeConnect.payouts.sectionFiltersLabel',
              'Filter by payout timeframe',
            )}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: 'flex-start', sm: 'center' },
              '& .MuiToggleButtonGroup-grouped': {
                border: 1,
                borderColor: 'divider',
                px: 1.5,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 700,
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: 'primary.light',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.22),
                  },
                },
              },
            }}
          >
            <ToggleButton value="past">
              {t('stripeConnect.payouts.past', 'Past')}
            </ToggleButton>
            <ToggleButton value="upcoming">
              {t('stripeConnect.payouts.upcoming', 'Upcoming')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {initialLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={140} px={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!initialLoading && error && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                {t('stripeConnect.payouts.retry', 'Retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {!initialLoading && !error && isEmpty && (
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'stripeConnect.payouts.empty',
              'No payouts yet. Transfers appear here after Stripe sends funds to your bank.',
            )}
          </Typography>
        </Box>
      )}

      {!initialLoading && !error && sectionEmpty && (
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {section === 'upcoming'
              ? t(
                  'stripeConnect.payouts.emptyUpcoming',
                  'No upcoming payouts right now.',
                )
              : t('stripeConnect.payouts.emptyPast', 'No past payouts yet.')}
          </Typography>
        </Box>
      )}

      {!initialLoading && !error && visiblePayouts.length > 0 && (
        <Stack
          divider={<Divider flexItem />}
          sx={{
            opacity: pageLoading ? 0.55 : 1,
            transition: 'opacity 120ms ease',
            pointerEvents: pageLoading ? 'none' : 'auto',
          }}
          aria-busy={pageLoading}
        >
          {visiblePayouts.map((payout) => (
            <PayoutRow
              key={payout.id}
              payout={payout}
              language={i18n.language}
              section={section}
            />
          ))}
        </Stack>
      )}

      {!error && !isEmpty && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <LedgerPagination
            id="stripe-payouts"
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            canGoPrevious={pageIndex > 0}
            canGoNext={hasMore && Boolean(nextStartingAfter)}
            onPrevious={handlePrevious}
            onNext={handleNext}
            disabled={busy}
            label={t('common.page', 'Page') + ` ${pageIndex + 1}`}
          />
        </Box>
      )}
    </Box>
  );
};

export default StripePayoutHistory;
